/**
 * <DataFreshness> — "Updated 12s ago" timestamp pattern.
 *
 * Resolves Phase-1 finding M12 — no canonical pattern for live-data
 * freshness despite WebSocket usage in 5+ files. Standardizes the
 * affordance across IoT sensor cards, dashboard live tiles, and
 * work-order assignment surfaces.
 *
 * Behavior:
 *   - Self-updates every `intervalMs` (default 30s) so the relative
 *     time stays fresh without the consumer wiring an interval.
 *   - Optional `staleAfterMs` flips the indicator dot from green to
 *     amber when data hasn't refreshed in time, then to red beyond
 *     `criticalAfterMs`. Maps to docs/design-system/11-domain-patterns.md.
 *   - `prefers-reduced-motion` skips the live indicator's pulse.
 *
 * Spec: docs/design-system/11-domain-patterns.md.
 */

import { useEffect, useState } from 'react';
import { cn } from '../utils/cn';
import { Text } from '../primitives/Text';

export type FreshnessTone = 'fresh' | 'stale' | 'critical' | 'offline';

export interface DataFreshnessProps {
  /** Timestamp the data was last updated (ms or Date). */
  updatedAt: number | Date | string;
  /** Polling interval to re-render. Default 30 000 ms. */
  intervalMs?: number;
  /** Beyond this age, render as 'stale'. Default 60 s. */
  staleAfterMs?: number;
  /** Beyond this age, render as 'critical'. Default 5 min. */
  criticalAfterMs?: number;
  /** Force a tone — used when consumer knows the connection is offline. */
  tone?: FreshnessTone;
  /** Show a pulsing dot indicating live connection. Default true. */
  showDot?: boolean;
  /** Prefix label. Default 'Updated'. */
  prefix?: string;
  className?: string;
}

const TONE_DOT: Record<FreshnessTone, string> = {
  fresh: 'bg-success',
  stale: 'bg-warning',
  critical: 'bg-danger',
  offline: 'bg-muted',
};

const TONE_TEXT: Record<FreshnessTone, 'success' | 'warning' | 'danger' | 'muted'> = {
  fresh: 'success',
  stale: 'warning',
  critical: 'danger',
  offline: 'muted',
};

function toMillis(updatedAt: DataFreshnessProps['updatedAt']): number {
  if (updatedAt instanceof Date) return updatedAt.getTime();
  if (typeof updatedAt === 'number') return updatedAt;
  return new Date(updatedAt).getTime();
}

function formatRelative(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

export function DataFreshness({
  updatedAt,
  intervalMs = 30_000,
  staleAfterMs = 60_000,
  criticalAfterMs = 5 * 60_000,
  tone: toneOverride,
  showDot = true,
  prefix = 'Updated',
  className,
}: DataFreshnessProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  const ts = toMillis(updatedAt);
  const ageMs = now - ts;
  const tone: FreshnessTone =
    toneOverride ??
    (ageMs >= criticalAfterMs ? 'critical' : ageMs >= staleAfterMs ? 'stale' : 'fresh');

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 motion-reduce:[&_.live-dot]:animate-none', className)}
      // Render an ISO timestamp in the tooltip for forensic detail.
      title={new Date(ts).toLocaleString()}
    >
      {showDot && (
        <span
          aria-hidden="true"
          className={cn(
            'live-dot inline-block h-1.5 w-1.5 rounded-full',
            TONE_DOT[tone],
            tone === 'fresh' && 'animate-pulse',
          )}
        />
      )}
      <Text variant="caption" tone={TONE_TEXT[tone]} as="span">
        {prefix} {formatRelative(ageMs)}
      </Text>
    </span>
  );
}

DataFreshness.displayName = 'DataFreshness';

export default DataFreshness;
